<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-7">
					<h3 class="page-header">
						<i class="fa fa-hand-o-right animated bounceInDown show-info"></i>
						Advances &amp; Loans
					</h3>
				</div>
				<div class="col-md-5 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="openForm">
						<i class="fa fa-plus"></i> Issue advance
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-3">
					<label>Status</label>
					<select class="form-control form-cascade-control input-box-short" v-model="status" @change="refresh">
						<option value="open">Open</option>
						<option value="closed">Closed</option>
						<option value="on_hold">On hold</option>
						<option value="">All</option>
					</select>
				</div>
				<div class="col-md-4">
					<label>Employee</label>
					<select
						class="form-control form-cascade-control input-box-short"
						v-model="employeeId"
						@change="refresh"
					>
						<option value="">All employees</option>
						<option v-for="person in employees" :key="person.id" :value="person.id">
							{{ person.name }}{{ person.employee_code ? ' · ' + person.employee_code : '' }}
						</option>
					</select>
				</div>
				<div class="col-md-3" style="margin-top:26px">
					<span class="text-muted">Outstanding</span>
					<strong style="margin-left:8px;font-size:16px">{{ money(totals.outstanding) }}</strong>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div v-if="error" class="alert alert-danger">{{ error }}</div>
			<div class="panel">
				<div class="panel-body" style="background:none">
					<div v-if="loading" class="text-muted">Loading…</div>

					<table v-else class="table table-bordered table-hover table-striped display">
						<thead class="table-font" style="background:#475c78;color:#fff">
							<th>#</th>
							<th>Employee</th>
							<th>Kind</th>
							<th>Issued</th>
							<th class="text-right">Principal</th>
							<th class="text-right">Instalment</th>
							<th class="text-right">Recovered</th>
							<th class="text-right">Outstanding</th>
							<th>Status</th>
							<th></th>
						</thead>
						<tbody class="table-font">
							<tr v-for="(row, index) in rows" :key="row.id">
								<td>{{ index + 1 }}</td>
								<td>{{ row.name }}</td>
								<td>
									<span :class="row.kind === 'loan' ? 'badge bg-info text-dark' : 'badge bg-secondary'">
										{{ row.kind === 'loan' ? 'Loan' : 'Advance' }}
									</span>
								</td>
								<td>{{ formatDate(row.issued_on) }}</td>
								<td class="text-right">{{ moneyShort(row.principal) }}</td>
								<td class="text-right">
									{{ moneyShort(row.installment_amount) }}
									<span v-if="row.kind === 'loan'" class="text-muted"> × {{ row.installments }}</span>
								</td>
								<!-- Settled in the ledger when given: nothing to recover on a slip. -->
								<template v-if="row.settle_via === 'ledger'">
									<td class="text-right text-muted">—</td>
									<td class="text-right text-muted">—</td>
									<td><span class="badge bg-info text-dark">In ledger</span></td>
									<td></td>
								</template>
								<template v-else>
									<td class="text-right">{{ moneyShort(row.recovered) }}</td>
									<td class="text-right">
										<strong :class="{ 'text-danger': row.outstanding > 0 }">
											{{ moneyShort(row.outstanding) }}
										</strong>
									</td>
									<td>{{ statusLabel(row.status) }}</td>
									<td>
										<button class="badge bg-primary" @click="showRecoveries(row)">Recoveries</button>
									</td>
								</template>
							</tr>
							<tr v-if="!rows.length">
								<td colspan="10" class="text-center text-muted" style="padding:24px">
									Nothing outstanding.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<modal name="payroll-advance" height="auto" :adaptive="true" width="480" :scrollable="true">
			<div style="padding:20px">
				<h4 class="modal-header-title">Issue an advance</h4>

				<div class="form-group">
					<label>Employee</label>
					<select class="form-control" v-model="form.employee_id">
						<option value="">Select</option>
						<option v-for="person in employees" :key="person.id" :value="person.id">
							{{ person.name }}{{ person.employee_code ? ' · ' + person.employee_code : '' }}
						</option>
					</select>
				</div>

				<div class="form-group">
					<label>Kind</label>
					<select class="form-control" v-model="form.kind">
						<option value="advance">
							{{
								settings.advance_in_ledger
									? 'Advance — posted to the ledger as a payment'
									: 'Advance — recovered in full from the next slip'
							}}
						</option>
						<option value="loan">Loan — recovered in instalments</option>
					</select>
				</div>

				<div class="row">
					<div class="col-md-6 form-group">
						<label>Amount</label>
						<input type="number" class="form-control" v-model="form.principal" />
					</div>
					<div class="col-md-6 form-group">
						<label>Given on</label>
						<input type="date" class="form-control" v-model="form.issued_on" />
					</div>
				</div>

				<template v-if="form.kind === 'loan'">
					<div class="row">
						<div class="col-md-6 form-group">
							<label>Instalments</label>
							<input type="number" class="form-control" v-model="form.installments" />
						</div>
						<div class="col-md-6 form-group">
							<label>Per instalment</label>
							<div class="form-control" style="background:#f3f5f8">{{ money(perInstalment) }}</div>
						</div>
					</div>
					<div class="row">
						<div class="col-md-6 form-group">
							<label>Start recovering from</label>
							<input type="month" class="form-control" v-model="form.recovery_start" />
						</div>
						<div class="col-md-6 form-group">
							<label>Max % of net pay</label>
							<input type="number" class="form-control" v-model="form.max_recovery_pct" />
							<!-- The cap that stops a big loan zeroing someone's take-home. -->
							<span class="text-muted" style="font-size:11px">
								Never take more than this share of a month's net.
							</span>
						</div>
					</div>
				</template>
				<p v-else-if="settings.advance_in_ledger" class="text-muted" style="font-size:12px">
					This company settles advances in the ledger: it is posted to the employee ledger as a
					payment on the date given, and no slip deducts it.
					<template v-if="settings.show_on_slip">
						The slip for that month shows it for information only.
					</template>
				</p>
				<p v-else class="text-muted" style="font-size:12px">
					An advance against this month's own salary comes off the next slip in full — you
					cannot decline to recover money already handed over for the month being paid.
				</p>

				<div class="form-group">
					<label>Expense entry id <span class="text-muted">(optional)</span></label>
					<input type="number" class="form-control" v-model="form.expense_transaction_id" />
					<span class="text-muted" style="font-size:11px">
						Links this to the cash that actually went out of the expense book.
					</span>
				</div>

				<div class="form-group">
					<label>Note</label>
					<input type="text" class="form-control" v-model="form.note" />
				</div>

				<div v-if="formError" class="alert alert-danger">{{ formError }}</div>
				<div class="text-right">
					<button class="btn btn-default" @click="$modal.hide('payroll-advance')">Cancel</button>
					<button class="btn btn-primary" style="margin-left:6px" @click="save" :disabled="saving">
						{{ saving ? 'Saving…' : 'Save' }}
					</button>
				</div>
			</div>
		</modal>

		<modal name="payroll-recoveries" height="auto" :adaptive="true" width="520">
			<div style="padding:20px">
				<h4 class="modal-header-title">Recoveries</h4>
				<table class="table table-bordered">
					<thead>
						<tr><th>Date</th><th>Slip</th><th class="text-right">Amount</th><th></th></tr>
					</thead>
					<tbody>
						<tr v-for="row in recoveries" :key="row.id" :class="{ reversed: row.reversed }">
							<td>{{ formatDate(row.recovered_on) }}</td>
							<td>#{{ row.slip_id }}</td>
							<td class="text-right">{{ money(row.amount) }}</td>
							<td class="text-muted">{{ row.reversed ? 'reversed' : '' }}</td>
						</tr>
						<tr v-if="!recoveries.length">
							<td colspan="4" class="text-center text-muted">Nothing recovered yet.</td>
						</tr>
					</tbody>
				</table>
				<div class="text-right">
					<button class="btn btn-default" @click="$modal.hide('payroll-recoveries')">Close</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { advanceService, errorMessage } from './payrollService'
import { employeeService } from '../attendance/attendanceService'
import payrollFormat from './payrollFormat'

const STATUS = {
	open: 'Open',
	closed: 'Closed',
	on_hold: 'On hold',
	written_off: 'Written off',
}

/**
 * Advances and loans, with what has actually been recovered against each.
 *
 * The screen this replaces had no such record at all: an advance was an expense
 * row with a flag, nothing ever marked it recovered, and the same amount came
 * off every slip for ever.
 */
export default {
	name: 'PayrollAdvances',
	mixins: [payrollFormat],
	data() {
		return {
			rows: [],
			totals: { outstanding: 0 },
			// The company's payroll_core advance setting, sent with the list.
			settings: { advance_in_ledger: false, show_on_slip: true },
			employees: [],
			recoveries: [],
			status: 'open',
			employeeId: '',
			loading: false,
			error: '',
			saving: false,
			formError: '',
			form: this.blank(),
		}
	},
	computed: {
		perInstalment() {
			const n = Number(this.form.installments || 1)
			return n > 0 ? Number(this.form.principal || 0) / n : 0
		},
	},
	mounted() {
		this.loadEmployees()
		this.refresh()
	},
	methods: {
		blank() {
			return {
				employee_id: '',
				kind: 'advance',
				principal: '',
				issued_on: new Date().toISOString().slice(0, 10),
				installments: 1,
				recovery_start: '',
				max_recovery_pct: 30,
				expense_transaction_id: '',
				note: '',
			}
		},
		statusLabel(status) {
			return STATUS[status] || status
		},
		refresh() {
			this.loading = true
			this.error = ''
			advanceService
				.list({
					status: this.status || undefined,
					employee_id: this.employeeId || undefined,
				})
				.then((data) => {
					this.rows = (data && data.data) || data || []
					this.totals = (data && data.totals) || { outstanding: 0 }
					if (data && data.settings) this.settings = data.settings
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load advances.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		loadEmployees() {
			employeeService
				.list()
				.then((data) => {
					this.employees = (data || []).filter((p) => p.is_active !== false)
				})
				.catch(() => {})
		},
		openForm() {
			this.form = this.blank()
			this.formError = ''
			this.$modal.show('payroll-advance')
		},
		save() {
			if (!this.form.employee_id || !(Number(this.form.principal) > 0)) {
				this.formError = 'Pick an employee and enter an amount.'
				return
			}
			this.saving = true
			this.formError = ''

			const body = {
				employee_id: this.form.employee_id,
				kind: this.form.kind,
				principal: Number(this.form.principal),
				issued_on: this.form.issued_on,
				note: this.form.note || undefined,
				expense_transaction_id: this.form.expense_transaction_id || undefined,
			}

			if (this.form.kind === 'loan') {
				body.installments = Number(this.form.installments || 1)
				body.recovery_start = this.form.recovery_start || undefined
				body.max_recovery_pct = Number(this.form.max_recovery_pct || 30)
			}

			advanceService
				.create(body)
				.then(() => {
					// Settled at once in ledger mode, so it leaves the Open list.
					const inLedger = this.settings.advance_in_ledger && body.kind === 'advance'
					this.$notify({
						text: inLedger
							? 'Advance recorded and posted to the ledger as a payment. It is listed under Closed.'
							: 'Advance recorded.',
						type: 'success',
					})
					this.$modal.hide('payroll-advance')
					this.refresh()
				})
				.catch((e) => {
					this.formError = errorMessage(e, 'Could not save that advance.')
				})
				.finally(() => {
					this.saving = false
				})
		},
		showRecoveries(row) {
			this.recoveries = []
			advanceService
				.recoveries(row.id)
				.then((data) => {
					this.recoveries = (data && data.recoveries) || []
					this.$modal.show('payroll-recoveries')
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not load recoveries.'), type: 'error' })
				})
		},
	},
}
</script>

<style scoped>
.text-right {
	text-align: right;
}
.modal-header-title {
	margin-top: 0;
}
.reversed td {
	opacity: 0.5;
	text-decoration: line-through;
}
</style>
